import { useState, useEffect, useCallback } from 'react';
import { 
  GroupedAccounts, 
  SocialAccount, 
  SocialPlatform, 
  SelectedAccounts 
} from '../types';
import { multiAccountService } from '../multiAccountService';
import { socialMediaService } from '../../../services/socialMediaService';
import { useNotification } from '../../../context/NotificationContext';

export const useMultiAccount = () => {
  const [groupedAccounts, setGroupedAccounts] = useState<GroupedAccounts>({
    youtube: [],
    instagram: [],
    tiktok: []
  });
  const [selectedAccounts, setSelectedAccounts] = useState<SelectedAccounts>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState<Partial<Record<SocialPlatform, boolean>>>({});
  
  const { showNotification } = useNotification();

  // Load accounts on mount
  useEffect(() => {
    loadAccounts();
  }, []);

  // Load saved selections when accounts change
  useEffect(() => {
    if (Object.values(groupedAccounts).some(accounts => accounts.length > 0)) {
      const defaults = multiAccountService.getDefaultAccounts(groupedAccounts);
      const saved = multiAccountService.loadSelectedAccounts();
      
      // Merge defaults with saved preferences, preferring saved
      const merged = { ...defaults, ...saved };
      setSelectedAccounts(merged);
    }
  }, [groupedAccounts]);

  const loadAccounts = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('Loading accounts...');
      
      // Try the new grouped approach first
      let accounts = await multiAccountService.getGroupedAccounts();
      console.log('Loaded grouped accounts:', accounts);
      
      // If no accounts found, try fallback to original service
      const totalAccounts = Object.values(accounts).flat().length;
      if (totalAccounts === 0) {
        console.log('No accounts from new service, trying fallback...');
        try {
          const legacyAccounts = await socialMediaService.getConnectedAccounts();
          console.log('Legacy accounts:', legacyAccounts);
          
          // Group the legacy accounts
          accounts = {
            youtube: legacyAccounts.filter(acc => acc.platform === 'youtube'),
            instagram: legacyAccounts.filter(acc => acc.platform === 'instagram'),
            tiktok: legacyAccounts.filter(acc => acc.platform === 'tiktok')
          };
          console.log('Grouped legacy accounts:', accounts);
        } catch (legacyError) {
          console.error('Legacy service also failed:', legacyError);
        }
      }
      
      setGroupedAccounts(accounts);
    } catch (error) {
      console.error('Error loading accounts:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to load social media accounts',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [showNotification]);

  const connectAccount = useCallback(async (platform: SocialPlatform) => {
    try {
      setIsConnecting(prev => ({ ...prev, [platform]: true }));
      
      // Use existing socialMediaService for connection
      const { url } = await socialMediaService.getAuthUrl(platform);
      const authSuccess = await socialMediaService.openAuthPopup(url, platform);
      
      if (authSuccess) {
        showNotification({
          title: 'Success',
          message: `${platform.charAt(0).toUpperCase() + platform.slice(1)} account connected successfully!`,
          type: 'success'
        });
        
        // Reload accounts to get the new one
        await loadAccounts();
      }
    } catch (error) {
      console.error(`Error connecting ${platform}:`, error);
      showNotification({
        title: 'Connection Failed',
        message: `Failed to connect ${platform} account. Please try again.`,
        type: 'error'
      });
    } finally {
      setIsConnecting(prev => ({ ...prev, [platform]: false }));
    }
  }, [showNotification, loadAccounts]);

  const setDefaultAccount = useCallback(async (account: SocialAccount) => {
    try {
      await multiAccountService.setDefaultAccount(account.id);
      
      showNotification({
        title: 'Default Set',
        message: `${multiAccountService.getAccountDisplayName(account)} is now the default ${account.platform} account`,
        type: 'success'
      });
      
      // Reload accounts to reflect the change
      await loadAccounts();
    } catch (error) {
      console.error('Error setting default account:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to set default account',
        type: 'error'
      });
    }
  }, [showNotification, loadAccounts]);

  const selectAccount = useCallback((platform: SocialPlatform, accountId: string) => {
    const newSelection = { ...selectedAccounts, [platform]: accountId };
    setSelectedAccounts(newSelection);
    multiAccountService.saveSelectedAccounts(newSelection);
  }, [selectedAccounts]);

  const getSelectedAccount = useCallback((platform: SocialPlatform): SocialAccount | null => {
    const accountId = selectedAccounts[platform];
    if (!accountId) return null;
    
    return multiAccountService.findAccountById(groupedAccounts, accountId);
  }, [selectedAccounts, groupedAccounts]);

  const hasActiveAccounts = useCallback((platform: SocialPlatform): boolean => {
    const accounts = groupedAccounts[platform] || [];
    return accounts.some(acc => acc.isActive && !acc.isExpired);
  }, [groupedAccounts]);

  const getActiveAccountsCount = useCallback((platform: SocialPlatform): number => {
    const accounts = groupedAccounts[platform] || [];
    return accounts.filter(acc => acc.isActive && !acc.isExpired).length;
  }, [groupedAccounts]);

  return {
    // State
    groupedAccounts,
    selectedAccounts,
    isLoading,
    isConnecting,
    
    // Actions
    loadAccounts,
    connectAccount,
    setDefaultAccount,
    selectAccount,
    
    // Helpers
    getSelectedAccount,
    hasActiveAccounts,
    getActiveAccountsCount
  };
};
